/**
 * TRAC-848: self-contained HTML renderer.
 *
 * "Self-contained" is literal — one file, inline CSS, no external stylesheet,
 * font, script, or image request. The customer can open it offline years from
 * now and it still renders. That constraint also makes it the right source for
 * the PDF path, which prints this exact document through the browser's own
 * engine rather than shipping a PDF library in the embed bundle.
 */

import type { ChatExport, ExportBlock, TireSnapshot } from './exportModel';
import {
  SPEAKER_LABELS,
  absoluteUrl,
  completenessWarning,
  formatDateTime,
  formatTime,
  priceAsOfSentence,
  ratingLine,
  tireName,
} from './format';

const TIRERACK_RED = '#ce1126';

/** Escape for HTML text and attribute contexts. Everything here is customer data. */
export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Prose arrives as Markdown from the agent. Rather than ship a Markdown parser
 * into the export path, handle the inline forms the agent actually emits and
 * escape everything else. Escaping happens FIRST, so no customer or agent text
 * can inject markup.
 */
/**
 * `[label](url)` in prose. The chat renders these through react-markdown as real
 * links, so printing the raw markdown is less faithful than the UI. Scheme is
 * restricted to http(s): the input is already escaped (so attribute injection is
 * out), but a `javascript:` destination must never become a live anchor — an
 * unrecognized scheme stays as literal text instead. Operates on ESCAPED text,
 * which is why it matches `&amp;` etc. inside the URL — that is valid in an href.
 */
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

const inlineMarkdown = (escaped: string): string =>
  escaped
    .replace(MD_LINK, '<a class="md-link" href="$2">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');

const renderProse = (text: string): string => {
  const blocks: string[] = [];
  let list: string[] = [];

  const flushList = () => {
    if (!list.length) return;
    blocks.push(`<ul class="prose-list">${list.map((li) => `<li>${li}</li>`).join('')}</ul>`);
    list = [];
  };

  for (const chunk of escapeHtml(text).split(/\n{2,}/)) {
    const lines = chunk.split('\n');
    let paragraph: string[] = [];

    const flushParagraph = () => {
      if (!paragraph.length) return;
      blocks.push(`<p>${inlineMarkdown(paragraph.join('<br>'))}</p>`);
      paragraph = [];
    };

    for (const line of lines) {
      const trimmed = line.trim();
      // The agent writes real Markdown; the chat UI renders it through
      // react-markdown, so an export that prints "- " and "---" literally is
      // less faithful than what the customer saw.
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
        flushParagraph();
        flushList();
        blocks.push('<hr>');
      } else if (/^[-*+]\s+/.test(trimmed)) {
        flushParagraph();
        list.push(inlineMarkdown(trimmed.replace(/^[-*+]\s+/, '')));
      } else {
        flushList();
        paragraph.push(line);
      }
    }
    flushParagraph();
  }
  flushList();

  return blocks.join('');
};

/**
 * Mirrors the chat's tire card: brand eyebrow, model, type, then price + rating.
 *
 * The WHOLE card is the link, not just the model name — MessageRenderer wraps the
 * entire card content in an `<a class={styles.tireCard}>`. Linking only the name
 * makes the rest of the card look dead when the customer clicks it.
 */
const renderTire = (tire: TireSnapshot): string => {
  const rating = ratingLine(tire);
  const body = `${tire.manufacturer ? `<span class="tire-brand">${escapeHtml(tire.manufacturer)}</span>` : ''}
      <span class="tire-name">${escapeHtml(tire.modelName ?? tireName(tire))}</span>
      ${tire.tireType ? `<span class="tire-type">${escapeHtml(tire.tireType)}</span>` : ''}
      ${tire.price ? `<span class="tire-price">${escapeHtml(tire.price)}</span>` : ''}
      ${rating ? `<span class="tire-rating">${escapeHtml(rating)}</span>` : ''}`;

  return tire.productUrl
    ? `<a class="tire" href="${escapeHtml(absoluteUrl(tire.productUrl))}">${body}</a>`
    : `<div class="tire">${body}</div>`;
};

const renderBlock = (block: ExportBlock): string => {
  switch (block.kind) {
    case 'prose':
      return `<div class="prose">${renderProse(block.text)}</div>`;

    case 'tires':
      return `<div class="tires">${block.tires.map(renderTire).join('')}</div>`;

    case 'link':
      // Mirrors the chat's shared LinkCard: eyebrow + prominent label on the left,
      // CTA button on the right.
      return `<div class="link">
        <div class="link-body">
          ${block.eyebrow ? `<span class="link-eyebrow">${escapeHtml(block.eyebrow)}</span>` : ''}
          <span class="link-label">${escapeHtml(block.label)}</span>
          ${block.blurb ? `<span class="blurb">${escapeHtml(block.blurb)}</span>` : ''}
        </div>
        <a class="link-cta" href="${escapeHtml(absoluteUrl(block.url))}">${escapeHtml(
          block.cta ?? 'Visit page →',
        )}</a>
      </div>`;

    case 'recommendations': {
      // Mirrors TDGLinkCard: a bold title, then a teal-bordered card headed by the
      // vehicle, a size/season detail row, and a full-width CTA. The card's
      // interactive priorities/tire-type pickers are dropped — they cannot work in
      // a saved document — but the link they'd have refined is preserved.
      const details = block.details
        .map(
          (d) =>
            `<span><span class="tdg-detail-label">${escapeHtml(d.label)}:</span> ${escapeHtml(d.value)}</span>`,
        )
        .join('');
      // The chip rows, drawn as the chat draws them — selected filled, unselected
      // dashed — so the reader sees what the recommendation was weighted toward
      // AND what it wasn't. They are a record, not controls: the "Customized
      // Recommendations" button is dropped because it cannot do anything here.
      const chipRow = (label: string, chips: { label: string; on: boolean }[]) =>
        chips.length
          ? `<div class="tdg-chip-row">
              <div class="tdg-chip-label">${escapeHtml(label)}</div>
              <div class="tdg-chips">${chips
                .map(
                  (c) =>
                    `<span class="tdg-chip${c.on ? ' on' : ''}">${escapeHtml(c.label)}</span>`,
                )
                .join('')}</div>
            </div>`
          : '';

      const on = block.priorities.filter((p) => p.on).map((p) => p.label);
      const priorities = `${
        on.length
          ? `<div class="tdg-current"><span class="tdg-current-label">Current Priorities:</span> ${escapeHtml(
              on.join(', '),
            )}</div>`
          : ''
      }`;
      const indicators = block.indicators.length
        ? `<div class="tdg-indicators">${block.indicators
            .map(
              (i) =>
                `<div class="tdg-indicator"><span class="tdg-indicator-label">${escapeHtml(
                  i.label,
                )}</span>${
                  i.description
                    ? `<span class="tdg-indicator-desc">${escapeHtml(i.description)}</span>`
                    : ''
                }</div>`,
            )
            .join('')}</div>`
        : '';

      return `<div class="tdg-title">${escapeHtml(block.title)}</div>
      <div class="tdg-card">
        ${block.vehicle ? `<div class="tdg-vehicle">${escapeHtml(block.vehicle)}</div>` : ''}
        ${details ? `<div class="tdg-details">${details}</div>` : ''}
        ${priorities}
        ${indicators}
        <a class="tdg-cta" href="${escapeHtml(absoluteUrl(block.url))}">${escapeHtml(block.cta)}</a>
        <div class="tdg-divider"></div>
        <div class="tdg-customize">What this recommendation was based on</div>
        ${chipRow('Priorities', block.priorities)}
        ${chipRow('Tire Type', block.tireTypes)}
      </div>`;
    }

    case 'sources': {
      // Matches the chat: a title, then a wrapping row of small bordered cards.
      const items = block.items
        .map(
          (item) =>
            `<a class="source" href="${escapeHtml(absoluteUrl(item.url))}">
              <span class="source-label">${escapeHtml(item.label)}</span>
              ${item.kind ? `<span class="source-kind">${escapeHtml(item.kind)}</span>` : ''}
            </a>`,
        )
        .join('');
      return `<div class="sources">${
        block.title ? `<div class="sources-head">${escapeHtml(block.title)}</div>` : ''
      }<div class="sources-grid">${items}</div></div>`;
    }

    case 'vehicle': {
      const size = block.staggered
        ? [block.frontSize && `front ${block.frontSize}`, block.rearSize && `rear ${block.rearSize}`]
            .filter(Boolean)
            .join(', ')
        : block.tireSize;
      const text = [block.description, size].filter(Boolean).join(' — ') || 'Vehicle confirmed';
      return `<p class="aside">${escapeHtml(text)}</p>`;
    }

    case 'rating': {
      const head = [
        block.label,
        typeof block.score === 'number' ? `${block.score} out of ${block.outOf}` : null,
        typeof block.reviewCount === 'number'
          ? `based on ${block.reviewCount.toLocaleString()} reviews`
          : null,
      ]
        .filter(Boolean)
        .join(' — ');
      const metrics = (block.metrics ?? [])
        .map(
          (m) =>
            `<li><span>${escapeHtml(m.label)}</span><span>${escapeHtml(m.subtitle)}</span></li>`,
        )
        .join('');
      return `<div class="rating"><div class="rating-head">${escapeHtml(head)}</div>${
        metrics ? `<ul class="rating-metrics">${metrics}</ul>` : ''
      }</div>`;
    }

    case 'options': {
      const items = block.items
        .map(
          (i) =>
            `<li><span class="opt-label">${escapeHtml(i.label)}</span>${
              i.subtitle ? `<span class="opt-sub">${escapeHtml(i.subtitle)}</span>` : ''
            }</li>`,
        )
        .join('');
      return `<div class="options">
        ${block.title ? `<div class="options-head">${escapeHtml(block.title)}</div>` : ''}
        <${block.numbered ? 'ol' : 'ul'} class="options-list">${items}</${block.numbered ? 'ol' : 'ul'}>
        ${block.hint ? `<div class="options-hint">${escapeHtml(block.hint)}</div>` : ''}
      </div>`;
    }

    case 'choice':
      return `<p class="aside">You chose: ${escapeHtml(block.label)}</p>`;

    case 'note':
      return `<p class="aside">${escapeHtml(block.text)}</p>`;

    case 'omitted':
      return `<p class="gap">An item shown here could not be included in this download.</p>`;
  }
};

/**
 * Every value below is lifted from the app's own stylesheets so the export reads
 * as the conversation the customer remembers rather than a generic document:
 * `ChatInterface.module.css` for the message bubbles (`.userMessage
 * .messageContent`), `MessageRenderer.module.css` for the source and tire cards,
 * `EmbeddableChat.module.css` for the header and BETA pill.
 *
 * The font stack is the app's, Gibson first. Gibson is deliberately NOT
 * webfont-loaded here — that would break the self-contained rule — so it renders
 * for readers who have it and falls back exactly as the chat does otherwise.
 */
const TEAL = '#2b7088';
const USER_BUBBLE = '#d5e2e7';
const FONT_STACK =
  'Gibson, -apple-system, system-ui, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Helvetica Neue", sans-serif';

const STYLES = `
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body {
    margin: 0; padding: 28px 16px 56px;
    font-family: ${FONT_STACK};
    font-size: 14px; line-height: 1.6;
    color: #19170d; background: #eef1f3;
  }
  .sheet { max-width: 760px; margin: 0 auto; background: #fff; border-radius: 12px;
           box-shadow: 0 2px 10px rgba(0,0,0,.10); overflow: hidden; }

  header { background: ${TEAL}; color: #fff; padding: 14px 20px;
           display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .brand { display: flex; align-items: center; font-size: 16px; font-weight: 600; letter-spacing: .3px; }
  .beta { display: inline-flex; align-items: center; padding: 3px 8px; background: #FBC946;
          color: #000; font-size: 10px; font-weight: 600; text-transform: uppercase;
          letter-spacing: .5px; border-radius: 12px; margin-left: 8px; line-height: 1; }
  /* Softened via text color, NOT opacity — element opacity forces the print
     engine to rasterize the stamp into a low-res bitmap (blurry, unselectable
     in the saved PDF); rgba keeps the glyphs vector. */
  .exported { font-size: 11px; color: rgba(255,255,255,.9); text-align: right; line-height: 1.35; }

  main { padding: 4px 20px 24px; }
  .banner { margin: 16px 0 0; padding: 11px 14px; border-radius: 8px;
            background: #fff5f5; border: 1px solid #f3c9c9; color: #8a1c1c; font-size: 13px; }
  .asof { margin: 14px 0 0; font-size: 11.5px; color: #6b7d84; }

  /* User turns: right-aligned bubble, geometry from .userMessage .messageContent. */
  .turn.customer { display: flex; flex-direction: column; align-items: flex-end; }
  .bubble { background: ${USER_BUBBLE}; color: ${TEAL}; padding: 10px 14px;
            border-radius: 16px 16px 4px 16px; font-size: 15px; font-weight: 500;
            line-height: 1.4; margin: 16px 0 0; width: fit-content; max-width: 82%; text-align: left; }
  .stamp { font-size: 11px; color: #6b7d84; font-weight: 600; margin-top: 4px; text-align: right; }

  /* Assistant turns: full-width, no bubble — .assistantMessage .messageContent. */
  .turn.traci, .turn.system, .turn.human-agent { margin: 14px 0 6px; color: #333; }
  .turn.human-agent .prose { border-left: 3px solid ${TIRERACK_RED}; padding-left: 12px; }

  .prose p { margin: 0 0 10px; }
  .prose p:last-child { margin-bottom: 0; }
  .prose-list { margin: 0 0 10px; padding-left: 20px; }
  .prose-list li { margin: 4px 0; }
  .prose hr { border: 0; border-top: 1px solid #e5e7eb; margin: 14px 0; }
  .prose strong { font-weight: 600; }
  /* In-prose markdown links — teal + underline, matching MessageRenderer's .link. */
  .prose .md-link { color: ${TEAL}; text-decoration: underline; overflow-wrap: anywhere; }

  /* Tire cards — .tireCardGrid / .tireCard. */
  .tires { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 12px 0; }
  .tire { background: #f9f9f9; border: 1px solid #e8e8e8; border-radius: 14px;
          padding: 12px 13px; display: flex; flex-direction: column;
          text-decoration: none; color: inherit; }
  .tire-brand { font-size: 9px; color: #888; text-transform: uppercase; letter-spacing: .03em; }
  .tire-name { font-size: 11.5px; font-weight: 600; color: #1a1a1a;
               display: block; margin-top: 1px; overflow-wrap: anywhere; }
  .tire-type { font-size: 9.5px; color: #888; margin-top: 3px; }
  .tire-price { font-size: 12.5px; font-weight: 700; color: #2a6496; margin-top: 8px; }
  .tire-rating { font-size: 10.5px; color: #6b7280; margin-top: 2px; }

  /* Source cards — .uiCardTitle + .sourceCardsGrid + .sourceCard. */
  .sources { margin: 12px 0; }
  .sources-head { font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 10px; }
  .sources-grid { display: flex; flex-wrap: wrap; gap: 8px; }
  .source { display: flex; flex-direction: column; gap: 2px; padding: 10px 14px;
            background: #f8f9fa; border: 1px solid #e5e7eb; border-radius: 8px;
            text-decoration: none; min-width: 120px; max-width: 200px; }
  .source-label { font-size: 13px; font-weight: 500; color: #1f2937; line-height: 1.3; overflow-wrap: anywhere; }
  .source-kind { font-size: 11px; color: #6b7280; text-transform: capitalize; }

  /* Link cards (size search, TDG, wayfinding) — the shared LinkCard visual. */
  .link { margin: 12px 0; padding: 12px 14px; background: #f8fafc;
          border: 1px solid #e2e8f0; border-radius: 8px;
          display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .link-body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .link-eyebrow { font-size: 10px; font-weight: 600; text-transform: uppercase;
                  letter-spacing: .04em; color: #6b7280; line-height: 1.2; }
  .link-label { font-size: 17px; font-weight: 600; color: #1f2937; line-height: 1.2; overflow-wrap: anywhere; }
  .link-cta { flex-shrink: 0; display: inline-block; padding: 7px 14px; background: ${TEAL};
              color: #fff; font-size: 13px; font-weight: 500; border-radius: 6px;
              text-decoration: none; white-space: nowrap; }
  .blurb { color: #6b7280; font-size: 11.5px; font-weight: 400; margin-top: 3px; }

  /* Recommendations (TDG) card — .tdgCard et al. */
  .tdg-title { font-size: 14px; font-weight: 600; color: #374151; margin: 12px 0 8px; }
  .tdg-card { background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
              border: 2px solid ${TEAL}; border-radius: 10px; padding: 14px;
              display: flex; flex-direction: column; gap: 12px; margin-bottom: 12px; }
  .tdg-vehicle { font-size: 16px; font-weight: 600; color: #1f2937; line-height: 1.3; }
  .tdg-details { display: flex; flex-wrap: wrap; gap: 18px; font-size: 13px; color: #1f2937; margin-top: -6px; }
  .tdg-detail-label { font-weight: 500; color: #6b7280; }
  .tdg-current { font-size: 13.5px; color: #1f2937; margin-top: -4px; }
  .tdg-current-label { font-weight: 600; }
  .tdg-divider { border-top: 1px solid #e5e7eb; }
  .tdg-customize { font-size: 13px; font-weight: 600; color: #374151; margin-top: -4px; }
  .tdg-chip-row { display: flex; flex-direction: column; gap: 6px; margin-top: -4px; }
  .tdg-chip-label { font-size: 12px; font-weight: 600; color: #6b7280; }
  .tdg-chips { display: flex; flex-wrap: wrap; gap: 8px; }
  /* Selected filled, unselected dashed — the chat's own chip states, minus the
     affordance: nothing here is clickable. */
  .tdg-chip { display: inline-block; padding: 6px 14px; border-radius: 999px;
              border: 1px dashed #d1d5db; color: #6b7280; font-size: 13px; font-weight: 600; }
  .tdg-chip.on { background: ${TEAL}; border: 1px solid ${TEAL}; color: #fff; }
  .tdg-indicators { display: flex; flex-direction: column; gap: 6px; }
  .tdg-indicator { font-size: 12.5px; color: #4b5563; }
  .tdg-indicator-label { font-weight: 600; color: #1f2937; }
  .tdg-indicator-desc { display: block; color: #6b7280; font-size: 11.5px; }
  .tdg-cta { display: block; text-align: center; padding: 12px; background: ${TEAL};
             color: #fff; font-size: 14px; font-weight: 600; border-radius: 6px;
             text-decoration: none; overflow-wrap: anywhere; }

  .rating { margin: 12px 0; padding: 12px 14px; border: 1px solid #e5e7eb;
            border-radius: 8px; background: #f8f9fa; }
  .rating-head { font-weight: 600; font-size: 13.5px; color: #374151; }
  .rating-metrics { list-style: none; margin: 8px 0 0; padding: 0;
                    display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 3px 16px; }
  .rating-metrics li { display: flex; justify-content: space-between; gap: 12px;
                       font-size: 12px; color: #4b5563; border-bottom: 1px dotted #e5e7eb; padding: 2px 0; }

  /* Option / ranked lists (selection_cards, ranking_cards). */
  .options { margin: 12px 0; }
  .options-head { font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px; }
  .options-list { margin: 0; padding-left: 20px; }
  .options-list li { margin: 4px 0; font-size: 13.5px; }
  .opt-label { color: #1f2937; font-weight: 500; }
  .opt-sub { color: #6b7280; font-size: 12px; margin-left: 6px; }
  .options-hint { font-size: 12px; color: #6b7280; margin-top: 6px; }

  /* Print-only appendix — see collectLinks(). */
  .link-index { display: none; }

  .aside { color: #6b7280; font-size: 12.5px; margin: 10px 0; }
  .gap { color: #8a1c1c; font-size: 12.5px; font-style: italic; margin: 10px 0; }
  .empty { color: #6b7280; font-style: italic; padding: 24px 0; }
  footer { padding: 16px 20px 22px; font-size: 11.5px; color: #6b7d84;
           border-top: 1px solid #e5e7eb; }
  footer a { color: ${TEAL}; }

  @media print {
    body { background: #fff; padding: 0; font-size: 10.5pt; }
    .sheet { max-width: none; box-shadow: none; border-radius: 0; }
    /* print-color-adjust inherits, so listing each background-bearing container
       covers its children (.beta in header, .link-cta in .link, .tdg-chip.on and
       .tdg-cta in .tdg-card). Omissions here print as bare text when the dialog's
       "print backgrounds" is off — the TDG card shipped that way. */
    header, .bubble, .source, .tire, .link, .rating, .tdg-card, .banner {
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    /* Keep individual cards intact, but NOT whole turns — a turn containing a
       tire grid rarely fits in what is left of a page, so avoiding breaks inside
       it pushes the whole thing over and leaves half-empty pages. */
    .tire, .source, .rating, .link, .bubble { break-inside: avoid; page-break-inside: avoid; }
    .sources-head { break-after: avoid; page-break-after: avoid; }
    a { text-decoration: none; }
    /* Buttons stay buttons on paper — stuffing the href into them via ::after
       turned a neat CTA into a wrapped blob of URL. Every destination is listed
       once in the appendix below instead. */
    .link-index { display: block; margin-top: 22px; padding-top: 14px; border-top: 1px solid #e5e7eb;
                  break-before: auto; }
    .link-index-head { font-size: 10pt; font-weight: 600; color: #374151; margin-bottom: 8px; }
    .link-index ol { margin: 0; padding-left: 18px; }
    .link-index li { font-size: 8.5pt; margin: 5px 0; break-inside: avoid; }
    /* One line per entry: black title, an em dash, then the URL as the ONLY
       anchor — blue, so it reads as the clickable part. The print engine carries
       link annotations into the saved PDF, so it genuinely is clickable there;
       on actual paper nothing is lost because the URL is the link text. */
    .li-label { color: #1f2937; font-weight: 500; }
    .li-url { color: #2a6496; overflow-wrap: anywhere; text-decoration: none; }
  }
`;

/**
 * Every destination in the conversation, deduped, in order. On screen the links
 * are clickable and an index would be noise, so it is print-only: source and tire
 * cards are far too narrow to carry a URL inline, and without this a printed copy
 * turns every card into a dead end.
 */
const collectLinks = (chat: ChatExport): { label: string; url: string }[] => {
  const seen = new Set<string>();
  const links: { label: string; url: string }[] = [];

  const add = (label: string, url: string) => {
    const absolute = absoluteUrl(url);
    if (seen.has(absolute)) return;
    seen.add(absolute);
    links.push({ label, url: absolute });
  };

  for (const turn of chat.turns) {
    for (const block of turn.blocks) {
      if (block.kind === 'link') add(block.label, block.url);
      else if (block.kind === 'recommendations') add(block.title, block.url);
      else if (block.kind === 'sources') block.items.forEach((i) => add(i.label, i.url));
      else if (block.kind === 'tires') {
        block.tires.forEach((t) => t.productUrl && add(tireName(t), t.productUrl));
      } else if (block.kind === 'prose') {
        // Markdown links in prose render label-only in the body, so on paper the
        // destination survives ONLY through this appendix.
        for (const m of block.text.matchAll(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g)) {
          add(m[1], m[2]);
        }
      }
    }
  }
  return links;
};

export function renderHtml(chat: ChatExport): string {
  const asOf = priceAsOfSentence(chat);
  const warning = completenessWarning(chat);
  const links = collectLinks(chat);

  const turns = chat.turns.length
    ? chat.turns
        .map((turn) => {
          // The customer's own words keep the chat's right-aligned bubble and
          // timestamp; everyone else renders full-width with no bubble, exactly
          // as ChatInterface does. Alignment carries the speaker, so no labels.
          if (turn.speaker === 'customer') {
            return `<section class="turn customer">
        <div class="bubble">${turn.blocks.map(renderBlock).join('')}</div>
        <div class="stamp">${escapeHtml(formatTime(turn.at))}</div>
      </section>`;
          }
          return `<section class="turn ${turn.speaker}" title="${escapeHtml(
            `${SPEAKER_LABELS[turn.speaker]} · ${formatTime(turn.at)}`,
          )}">${turn.blocks.map(renderBlock).join('')}</section>`;
        })
        .join('')
    : '<p class="empty">This conversation is empty.</p>';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Your Tire Rack chat</title>
<style>${STYLES}</style>
</head>
<body>
  <div class="sheet">
    <header>
      <span class="brand">AI Assist<span class="beta">Beta</span></span>
      <span class="exported">Your saved chat<br>${escapeHtml(formatDateTime(chat.exportedAt))}</span>
    </header>
    <main>
      ${warning ? `<div class="banner">${escapeHtml(warning)}</div>` : ''}
      ${asOf ? `<p class="asof">${escapeHtml(asOf)}</p>` : ''}
      ${turns}
      ${
        links.length
          ? `<section class="link-index">
        <div class="link-index-head">Links in this chat</div>
        <ol>${links
          .map(
            (l) =>
              `<li><span class="li-label">${escapeHtml(l.label)}</span> — <a class="li-url" href="${escapeHtml(
                l.url,
              )}">${escapeHtml(l.url)}</a></li>`,
          )
          .join('')}</ol>
      </section>`
          : ''
      }
    </main>
    <footer>
      Saved from your chat with AI Assist at <a href="https://www.tirerack.com">tirerack.com</a>.
      Prices and availability change; check the product page for current details.
    </footer>
  </div>
</body>
</html>`;
}
